import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const DepreciationAnalysis = ({ results, craneData }) => {
  const [chartView, setChartView] = useState('projection'); // 'projection' or 'comparison'
  
  if (!results) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(amount);
  };

  // Generate depreciation projection data
  const generateDepreciationData = () => {
    const currentYear = new Date()?.getFullYear();
    const craneAge = currentYear - (craneData?.year || 2020);
    const currentValue = results?.fairMarketValue;
    const annualDepreciation = results?.depreciationRate / 100;
    
    const data = [];
    
    // Historical data (if crane is not brand new)
    for (let i = Math.max(0, 5 - craneAge); i >= 0; i--) {
      const year = currentYear - i;
      const value = currentValue / Math.pow(1 - annualDepreciation, -i);
      data?.push({
        year: year?.toString(),
        value: Math.round(value),
        age: craneAge + i,
        type: i === 0 ? 'current' : 'historical'
      });
    }
    
    // Future projections
    for (let i = 1; i <= 10; i++) {
      const year = currentYear + i;
      const value = currentValue * Math.pow(1 - annualDepreciation, i);
      data?.push({
        year: year?.toString(),
        value: Math.round(value),
        age: craneAge + i,
        type: 'projection'
      });
    }
    
    return data;
  };

  // Generate market comparison data
  const generateComparisonData = () => {
    const craneTypes = [
      { type: 'Mobile Crane', depreciation: 8.5, color: '#3B82F6' },
      { type: 'Rough Terrain', depreciation: 9.2, color: '#10B981' },
      { type: 'All Terrain', depreciation: 7.8, color: '#F59E0B' },
      { type: 'Crawler', depreciation: 6.5, color: '#EF4444' },
      { type: 'Your Crane', depreciation: results?.depreciationRate, color: '#8B5CF6' }
    ];
    
    return craneTypes?.map(item => ({
      ...item,
      value5yr: Math.round(1000000 * Math.pow(1 - item?.depreciation / 100, 5)),
      value10yr: Math.round(1000000 * Math.pow(1 - item?.depreciation / 100, 10))
    }));
  };

  const depreciationData = generateDepreciationData();
  const comparisonData = generateComparisonData();

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-surface border border-border rounded-lg shadow-lg p-3">
          <p className="text-text-primary font-medium">{`Year: ${label}`}</p>
          <p className="text-text-primary">{`Value: ${formatCurrency(payload?.[0]?.value)}`}</p>
          <p className="text-text-secondary text-sm">{`Age: ${data?.age} years`}</p>
        </div>
      );
    }
    return null;
  };

  const ComparisonTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-surface border border-border rounded-lg shadow-lg p-3">
          <p className="text-text-primary font-medium">{label}</p>
          <p className="text-text-primary">{`5-Year: ${formatCurrency(payload?.[0]?.value)}`}</p>
          <p className="text-text-secondary text-sm">{`Annual Rate: ${comparisonData?.find(d => d?.type === label)?.depreciation}%`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Depreciation Overview */}
      <div className="bg-surface border border-border rounded-lg shadow-card p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="TrendingDown" size={24} color="white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-text-primary">Depreciation Analysis</h2>
              <p className="text-text-secondary">Value trends and market projections</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant={chartView === 'projection' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setChartView('projection')}
            >
              Value Projection
            </Button>
            <Button
              variant={chartView === 'comparison' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setChartView('comparison')}
            >
              Market Comparison
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="text-center p-4 bg-muted rounded-lg">
            <div className="text-2xl font-bold text-primary mb-1">
              {results?.depreciationRate?.toFixed(1)}%
            </div>
            <div className="text-sm font-medium text-text-primary">Annual Rate</div>
          </div>
          
          <div className="text-center p-4 bg-muted rounded-lg">
            <div className="text-lg font-semibold text-text-primary mb-1">
              {formatCurrency(results?.fairMarketValue * Math.pow(1 - results?.depreciationRate / 100, 5))}
            </div>
            <div className="text-sm font-medium text-text-primary">5-Year Value</div>
          </div>
          
          <div className="text-center p-4 bg-muted rounded-lg">
            <div className="text-lg font-semibold text-text-primary mb-1">
              {formatCurrency(results?.fairMarketValue * Math.pow(1 - results?.depreciationRate / 100, 10))}
            </div>
            <div className="text-sm font-medium text-text-primary">10-Year Value</div>
          </div>
          
          <div className="text-center p-4 bg-muted rounded-lg">
            <div className="text-lg font-semibold text-text-primary mb-1">
              {Math.round((1 - Math.pow(1 - results?.depreciationRate / 100, 10)) * 100)}%
            </div>
            <div className="text-sm font-medium text-text-primary">Total 10-Yr Loss</div>
          </div>
        </div>

        {/* Chart Display */}
        <div className="h-80">
          {chartView === 'projection' ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={depreciationData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis 
                  dataKey="year" 
                  stroke="#6B7280"
                  fontSize={12}
                />
                <YAxis 
                  stroke="#6B7280"
                  fontSize={12}
                  tickFormatter={(value) => `$${(value / 1000000)?.toFixed(1)}M`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line 
                  type="monotone" 
                  dataKey="value" 
                  stroke="#3B82F6" 
                  strokeWidth={3}
                  dot={(props) => {
                    const { payload } = props;
                    if (payload?.type === 'current') {
                      return <circle {...props} fill="#EF4444" stroke="#EF4444" strokeWidth={2} r={6} />;
                    }
                    return <circle {...props} fill="#3B82F6" stroke="#3B82F6" strokeWidth={2} r={4} />;
                  }}
                  activeDot={{ r: 8, stroke: '#3B82F6', strokeWidth: 2, fill: '#3B82F6' }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={comparisonData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis 
                  dataKey="type" 
                  stroke="#6B7280"
                  fontSize={12}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  stroke="#6B7280"
                  fontSize={12}
                  tickFormatter={(value) => `$${(value / 1000000)?.toFixed(1)}M`}
                />
                <Tooltip content={<ComparisonTooltip />} />
                <Bar 
                  dataKey="value5yr" 
                  fill="#3B82F6"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
      {/* Depreciation Factors */}
      <div className="bg-surface border border-border rounded-lg shadow-card p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center space-x-2">
          <Icon name="Target" size={20} className="text-accent" />
          <span>Depreciation Factors</span>
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="font-medium text-text-primary">Accelerating Factors</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <span className="text-sm text-red-800">High Operating Hours</span>
                <Icon name="TrendingUp" size={16} className="text-red-600" />
              </div>
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <span className="text-sm text-red-800">Poor Maintenance</span>
                <Icon name="TrendingUp" size={16} className="text-red-600" />
              </div>
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <span className="text-sm text-red-800">Harsh Environment</span>
                <Icon name="TrendingUp" size={16} className="text-red-600" />
              </div>
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <span className="text-sm text-red-800">Technology Obsolescence</span>
                <Icon name="TrendingUp" size={16} className="text-red-600" />
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <h4 className="font-medium text-text-primary">Mitigating Factors</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <span className="text-sm text-green-800">Regular Maintenance</span>
                <Icon name="TrendingDown" size={16} className="text-green-600" />
              </div>
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <span className="text-sm text-green-800">Premium Brand</span>
                <Icon name="TrendingDown" size={16} className="text-green-600" />
              </div>
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <span className="text-sm text-green-800">Complete Documentation</span>
                <Icon name="TrendingDown" size={16} className="text-green-600" />
              </div>
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <span className="text-sm text-green-800">Strong Market Demand</span>
                <Icon name="TrendingDown" size={16} className="text-green-600" />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Optimal Selling Timeframe */}
      <div className="bg-surface border border-border rounded-lg shadow-card p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center space-x-2">
          <Icon name="Calendar" size={20} className="text-accent" />
          <span>Optimal Selling Strategy</span>
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="font-medium text-text-primary">Seasonal Considerations</h4>
            <div className="space-y-3">
              <div className="p-3 bg-muted rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-text-primary">Spring (Mar-May)</span>
                  <span className="text-sm font-semibold text-success">+5% to +8%</span>
                </div>
                <p className="text-xs text-text-secondary">Peak construction season, highest demand</p>
              </div>
              
              <div className="p-3 bg-muted rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-text-primary">Summer (Jun-Aug)</span>
                  <span className="text-sm font-semibold text-success">+2% to +5%</span>
                </div>
                <p className="text-xs text-text-secondary">Strong activity, good selling conditions</p>
              </div>
              
              <div className="p-3 bg-muted rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-text-primary">Fall (Sep-Nov)</span>
                  <span className="text-sm font-semibold text-primary">Baseline</span>
                </div>
                <p className="text-xs text-text-secondary">Moderate activity, steady pricing</p>
              </div>
              
              <div className="p-3 bg-muted rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-text-primary">Winter (Dec-Feb)</span>
                  <span className="text-sm font-semibold text-warning">-3% to -7%</span>
                </div>
                <p className="text-xs text-text-secondary">Reduced activity, buyer's market</p>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <h4 className="font-medium text-text-primary">Market Timing</h4>
            <div className="space-y-3">
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="CheckCircle" size={16} className="text-green-600" />
                  <span className="text-sm font-medium text-green-800">Optimal Window</span>
                </div>
                <p className="text-sm text-green-700">
                  Next 6-12 months appears favorable based on current market conditions and your crane's age/condition profile.
                </p>
              </div>
              
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Info" size={16} className="text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">Market Outlook</span>
                </div>
                <p className="text-sm text-blue-700">
                  Infrastructure spending and construction activity expected to remain strong, supporting equipment values.
                </p>
              </div>
              
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="AlertTriangle" size={16} className="text-amber-600" />
                  <span className="text-sm font-medium text-amber-800">Watch Factors</span>
                </div>
                <p className="text-sm text-amber-700">
                  Monitor interest rates and new equipment lead times, which can impact used equipment demand.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepreciationAnalysis;